// src/components/clients/ColumnManager.jsx
import React, { useEffect, useMemo, useState } from "react";

/**
 * ColumnManager
 * Modal to show/hide and reorder columns.
 *
 * Props:
 *   columns     : Array<{ id, label, visible: bool }>
 *   onChange    : (nextColumns) => void   // returns the updated array
 *   storageKey  : string                  // optional; persists to localStorage
 *   onClose     : () => void
 */
export default function ColumnManager({
  columns,
  onChange,
  onClose,
  storageKey = "hypercrm.clients.columns",
}) {
  const [local, setLocal] = useState(columns || []);

  useEffect(() => setLocal(columns || []), [columns]);

  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(local));
    } catch {
      /* ignore */
    }
  }, [local, storageKey]);

  const move = (index, delta) => {
    setLocal((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next;
    });
  };

  const toggle = (id) => {
    setLocal((prev) =>
      prev.map((c) => (c.id === id ? { ...c, visible: !c.visible } : c))
    );
  };

  const allOn = useMemo(() => local.every((c) => c.visible), [local]);
  const setAll = (v) => setLocal((prev) => prev.map((c) => ({ ...c, visible: v })));

  const save = () => {
    onChange?.(local);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
      <div className="w-[560px] max-w-[92vw] rounded-2xl border border-gray-200 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <div className="text-base font-semibold text-gray-900">Manage Columns</div>
            <div className="text-xs text-gray-500">Show, hide, and reorder table columns.</div>
          </div>
          <button
            className="rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <div className="p-5">
          <div className="mb-3 flex items-center gap-3">
            <button
              className="rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50"
              onClick={() => setAll(true)}
              disabled={allOn}
              title="Show all"
            >
              Show all
            </button>
            <button
              className="rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50"
              onClick={() => setAll(false)}
              title="Hide all"
            >
              Hide all
            </button>
          </div>

          <ul className="max-h-[50vh] overflow-auto divide-y rounded-lg border">
            {local.map((c, idx) => (
              <li key={c.id} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={!!c.visible}
                    onChange={() => toggle(c.id)}
                  />
                  <span className="text-sm text-gray-800">{c.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="rounded border px-2 py-1 text-xs hover:bg-gray-50"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button
                    className="rounded border px-2 py-1 text-xs hover:bg-gray-50"
                    onClick={() => move(idx, +1)}
                    disabled={idx === local.length - 1}
                    title="Move down"
                  >
                    ↓
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center justify-end gap-2 border-t px-5 py-4">
          <button
            className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            onClick={save}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
